// src/components/SidebarPrompt.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function SidebarPrompt({ 
  user,
  chatHistory,
  isHistoryLoading,
  currentSessionId,
  usageStats,
  planTracker,
  dynamicQRLimit,
  dynamicQRUsed,
  onNewChat,
  onLoadSession,
  onDeleteSession,
  onLogout,
  getRelativeTime
}) {
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const handleDeleteClick = (sessionId, e) => {
    e.stopPropagation();
    onDeleteSession(sessionId, e);
  };

  if (!user) return null;

  return (
    <div className={`sidebar ${sidebarExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-content">
        <div className="sidebar-top">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <img src="/headerdark.png" alt="Queret.ai Logo" className="logo-image-sidebar" />
              {sidebarExpanded && <h1 className="logo-text-sidebar">Queret.ai</h1>}
            </div>
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {sidebarExpanded && (
            <>
              <button className="new-chat-btn" onClick={onNewChat}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>New Chat</span>
              </button>

              <div className="chat-history">
                <div className="chat-history-header">Recent Chats</div>
                
                {isHistoryLoading ? (
                  <div className="history-loading">
                    <div className="spinner-small"></div>
                    <span>Loading history...</span>
                  </div>
                ) : chatHistory.length > 0 ? (
                  chatHistory.map(chat => (
                    <div 
                      key={chat.id} 
                      className={`chat-item ${currentSessionId === chat.id ? 'active' : ''}`}
                      onClick={() => onLoadSession(chat)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div className="chat-item-content">
                        <div className="chat-item-title">{chat.title}</div>
                        <div className="chat-item-time">{getRelativeTime(chat.timestamp)}</div>
                      </div>
                      <button 
                        className="delete-session-btn"
                        onClick={(e) => handleDeleteClick(chat.id, e)}
                        title="Delete session"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-history">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>No chat history yet</p>
                    <span>Your sessions will appear here</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="sidebar-bottom">
          {sidebarExpanded ? (
            <>
              {user && usageStats && !planTracker?.loading && (
                <div className="sidebar-usage-card">
                  <div className="sidebar-usage-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{planTracker.planConfig.name}</span>
                  </div>
                  <div className="sidebar-usage-stats">
                    <div className="sidebar-usage-item">
                      <span className="sidebar-usage-label">QR Used</span>
                      <span className="sidebar-usage-value">{usageStats.qrGenerated}</span>
                    </div>
                    <div className="sidebar-usage-item">
                      <span className="sidebar-usage-label">QR Left</span>
                      <span className="sidebar-usage-value">
                        {typeof usageStats.qrRemaining === 'number' 
                          ? usageStats.qrRemaining 
                          : usageStats.qrRemaining}
                      </span>
                    </div>
                  </div>
                  {dynamicQRLimit > 0 && (
                    <div className="sidebar-usage-stats" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <div className="sidebar-usage-item">
                        <span className="sidebar-usage-label">⚡ Dynamic</span>
                        <span className="sidebar-usage-value">
                          {dynamicQRUsed}/{dynamicQRLimit === -1 ? '∞' : dynamicQRLimit}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="sidebar-usage-progress">
                    <div 
                      className="sidebar-usage-progress-bar"
                      style={{
                        width: `${typeof usageStats.qrRemaining === 'number' 
                          ? (usageStats.qrGenerated / planTracker.planConfig.limits.qrPerMonth) * 100 
                          : 0}%`
                      }}
                    />
                  </div>
                  <button 
                    className="sidebar-upgrade-btn"
                    onClick={() => navigate('/pricing-plans')}
                  >
                    ⚡ Upgrade Plan
                  </button>
                </div>
              )}
              
              <div className="profile-dropdown-container">
                <button 
                  className="sidebar-menu-item user-profile"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                >
                  <div className="user-avatar">
                    {user?.email ? (
                      <span className="user-initials">
                        {user.email.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">
                      {user?.displayName || user?.email?.split('@')[0] || 'Guest User'}
                    </div>
                    <div className="user-email">
                      {user?.email || 'Not signed in'}
                    </div>
                  </div>
                  <svg className="user-dropdown" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {showProfileDropdown && (
                  <div className="profile-dropdown-menu">
                    <button className="profile-dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Get Help
                    </button>
                    <button className="profile-dropdown-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 16v-4m0-4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Learn More
                    </button>
                    <div className="profile-dropdown-divider"></div>
                    <button className="profile-dropdown-item logout" onClick={onLogout}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14l5-5-5-5m5 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <button 
                className="sidebar-icon-btn" 
                title="Pricing"
                onClick={() => navigate('/pricing-plans')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </button>
              <button 
                className="sidebar-icon-btn" 
                title="User Settings"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                {user?.email ? (
                  <span className="user-initials-small">
                    {user.email.charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SidebarPrompt;